import { ConnectButton } from "@/components/connect-button";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { makeSetMetaTransactions } from "@/lib/clients";
import { N, shorten } from "@/lib/utils";
import { BenchMeta, BenchStateFull } from "@myth-finance/bench-sdk";
import { useWallet } from "@txnlab/use-wallet";
import { Save, UserRound, ChevronRight } from "lucide-react";
import { BenchRegistryClient } from "@myth-finance/bench-sdk";

export interface EditMetaFormData {
  termsAndConditions: string;
  blockedCountries: string[];
}

export function EditMetaButton({
  data,
  reloadState,
  onSuccess,
  formData,
  disabled = false,
  className,
}: {
  data: BenchStateFull;
  reloadState: () => any;
  onSuccess: () => any;
  formData: EditMetaFormData;
  disabled?: boolean;
  className?: string;
}) {
  const { activeAddress } = useWallet();

  const hasChanges = () => {
    const currentTc = data.meta?.tc || "";
    const currentBc = data.meta?.bc || [];

    return (
      formData.termsAndConditions !== currentTc ||
      JSON.stringify(formData.blockedCountries?.sort()) !==
        JSON.stringify(currentBc?.sort())
    );
  };

  return (
    <TransactionDialog
      type="purchase"
      trigger={(onClick) => (
        <ConnectButton
          onClick={onClick}
          disabled={disabled || !hasChanges()}
          className={className}
        >
          <Save size={18} className="mr-1.5" />
          <span>Save Changes</span>
        </ConnectButton>
      )}
      transactionBuilder={() => {
        const { blockedCountries, termsAndConditions } = formData;
        const meta: BenchMeta | null =
          blockedCountries.length || termsAndConditions
            ? {
                ...(blockedCountries.length ? { bc: blockedCountries } : null),
                ...(termsAndConditions ? { tc: termsAndConditions } : null),
              }
            : null;
        return makeSetMetaTransactions({
          sender: activeAddress!,
          registryKey: BenchRegistryClient.getRegistryKey(data),
          meta,
        });
      }}
      title="Update Bench Terms & Countries"
      onClose={() => reloadState()}
      onSuccess={() => onSuccess()}
    >
      {transactionInner(
        "operation",
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Updating bench settings with the following changes:
          </div>
          {formData.termsAndConditions !== (data.meta?.tc || "") && (
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-sm font-medium">Terms & Conditions:</div>
              <div className="text-sm text-muted-foreground break-all">
                {formData.termsAndConditions || "(removed)"}
              </div>
            </div>
          )}
          {JSON.stringify(formData.blockedCountries?.sort()) !==
            JSON.stringify((data.meta?.bc || [])?.sort()) && (
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-sm font-medium">Restricted Countries:</div>
              <div className="text-sm text-muted-foreground">
                {formData.blockedCountries.length > 0
                  ? formData.blockedCountries.join(", ")
                  : "(none)"}
              </div>
            </div>
          )}
        </div>,
        <>
          <div className="flex justify-between gap-2 mb-2">
            <div className="flex items-center">
              <UserRound size={18} className="mr-1" /> Account
            </div>{" "}
            <span>{shorten(activeAddress!)}</span>
          </div>
          <div className="flex justify-between gap-2 text-foreground">
            <div className="flex items-center">
              <ChevronRight size={18} className="mr-1" /> Update bench meta
            </div>
            <span>{N(data.appId)}</span>
          </div>
        </>
      )}
    </TransactionDialog>
  );
}
